import { Box, Container, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import Button from '~/components/common/Button';
import { useGetTeamsQuery } from '~/services/api/shared';
import { IDriverInfo } from '~/models/shared';
import { useDeleteTeamMutation } from '~/services/api/team';
import { showAlert, alertParams } from '~/components/common/Alert';
import { errorAlert } from '~/components/common/Alert/alertParams';
import { queryClient } from '~/lib/react-query';
import { queryKeys } from '~/services/api/queryKeys';
import CircularLoading from '~/components/common/CirularLoading';
import { ToolTipText } from '~/components/common/Tooltip/TooltipText';
import { formatPhoneNumber } from '~/utils/formatter';
import { useFirstLoadingDetection } from '~/hooks';
import { usePaymentInfo } from '~/providers/PaymentProvider';
import { useLocation } from 'react-router-dom';
import { fromQueryString } from '~/utils/queryString';
import { DriverAvatar } from '~/components/shared/AssignTask/DriverAvatar';
import AddIcon from '~/assets/images/icons/add-white.svg';
import themes from '~/themes';
import { ElementSelect } from '~/components/common/ElementSelect/ElementSelect';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import useStyles from './style';
import SetupYourTeams from './SetupYourTeams';
import { DialogAddTeam } from '../AddTeam/DialogAddTeam';

const EmptyStateData = [
  {
    name: 'Day Shift',
    description: 'This team works 8am - 4pm',
    hub_id: null,
    org_id: '64489c4244de8f93841ef993',
    driver_ids: ['649d230515bb7004b31ecca0'],
    created_at: '2023-07-20T09:37:46.165000',
    updated_at: '2023-07-20T09:37:46.165000',
    hub_serve: [],
    driver_info: [
      {
        display_name: 'John Doe',
        phone: '11010101010',
        email: null,
        driver_status: 'offline',
        lat: 21.0455626,
        lng: 105.843085,
        id: '649d230515bb7004b31ecca0',
      },
      {
        display_name: 'Jane Doe',
        phone: '11010101010',
        email: null,
        driver_status: 'offline',
        lat: 21.0455626,
        lng: 105.843085,
        id: '649d230515bb7004b31ecca0',
      },
    ],
    id: '64b9006a1d71b94711ab88ad',
  },
];

type TeamListProps = {};

export const TeamList: React.FC<TeamListProps> = () => {
  const location = useLocation();
  const queries = fromQueryString(location.search);
  const searchTeamQuery = (queries?.search || '') as string;
  const [openAddTeamDialog, setOpenAddTeamDialog] = useState(false);
  const [teamEditData, setTeamEditData] = useState(null);

  const toggleOpenAddTeamDialog = () => setOpenAddTeamDialog((v) => !v);
  const { isStarterOrStandardPlan, setIsOpenUpSellDialog } = usePaymentInfo();

  const { data: rawData, isFetching: isFetchingTeams } = useGetTeamsQuery({
    enabled: !isStarterOrStandardPlan,
    params: {
      search: searchTeamQuery,
    },
  });

  const handleEditTeam = (team) => {
    toggleOpenAddTeamDialog();
    setTeamEditData(team);
  };

  const classes = useStyles();

  const teams = useMemo(() => rawData || [], [rawData]);
  const isFirstLoading = useFirstLoadingDetection([isFetchingTeams]);

  const { mutate: deleteTeamMutation } = useDeleteTeamMutation({
    onSuccess: ({ team_id }) => {
      queryClient.invalidateQueries([queryKeys.shared.getTeamList]);
      queryClient.setQueriesData(
        [queryKeys.shared.getTeamList],
        (oldData: any) => oldData.filter((data) => data.id !== team_id),
      );
      showAlert({
        ...alertParams.success,
        title: 'Team Deleted Successfully',
      });
      setTeamEditData(null);
    },
    onError: (error) => {
      showAlert(errorAlert(error));
    },
  });

  const isEmptyState = !teams?.length && !isFetchingTeams && !searchTeamQuery;

  const renderDriversInTeam = ({
    driver_info,
    name,
  }: {
    driver_info: IDriverInfo[];
    name: string;
  }) => (
    <>
      <Typography fontWeight={600} mb={0.8}>
        Drivers ({driver_info?.length || 0})
      </Typography>
      <Stack
        data-testid={`${name}-driver-list-in-team`}
        gap={0.8}
        sx={{
          flexDirection: 'row',
          flexWrap: 'wrap',
        }}
      >
        {driver_info.map((driver) => (
          <Stack
            sx={{
              backgroundColor: '#F4F5F9',
              borderRadius: '4px',
              padding: '4px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
              width: 'fit-content',
            }}
          >
            {isEmptyState ? (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 15,
                  backgroundColor: 'rgba(0,0,0,0.1)',
                }}
              />
            ) : (
              <DriverAvatar
                name={driver.display_name}
                size={24}
                avatar={driver.avatar}
              />
            )}
            <ToolTipText text={driver.display_name} maxLength={20}>
              <span data-testid='driver-name'>
                {driver.display_name.length > 20
                  ? `${driver.display_name.slice(0, 20)}...`
                  : driver.display_name}
              </span>
            </ToolTipText>
            <Typography
              data-testid='driver-phone'
              sx={{
                color: '#717D96',
              }}
            >
              {isEmptyState
                ? '(+1) 101-010-1010'
                : formatPhoneNumber(driver.phone)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </>
  );

  const teamListComponent = useMemo(() => {
    if (!teams.length && !isEmptyState)
      return (
        <Typography textAlign='center' mt={4}>
          No results found.
        </Typography>
      );

    return (isEmptyState ? EmptyStateData : teams).map((team) => {
      const { name, description } = team;
      return (
        <Stack
          sx={{
            borderRadius: '4px',
            border: '1px solid #E2E7F0',
            padding: '16px 24px',
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.6,
            ...(isEmptyState && {
              opacity: 0.5,
            }),
          }}
        >
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'start',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 500,
                }}
              >
                {name}
              </Typography>
              <Typography>{description}</Typography>
            </Box>
            {!isEmptyState && (
              <ElementSelect
                paperProps={{
                  sx: {
                    width: 160,
                  },
                }}
                elementSelect={() => (
                  <MoreHorizIcon
                    sx={{ fontSize: 20, color: themes.color.violet900 }}
                  />
                )}
                iconButtonProps={{
                  sx: { height: 26, width: 26 },
                }}
                onChange={(o) => {
                  if (o.value === 'edit') {
                    handleEditTeam(team);
                  }
                  if (o.value === 'delete') {
                    showAlert({
                      ...alertParams.warning,
                      title: 'Warning',
                      cancelText: 'Cancel',
                      okText: 'Yes, Delete',
                      description: (
                        <>
                          You are about to delete this team.
                          <br />
                          Are you sure you want to continue?
                        </>
                      ),
                      onOk: () => {
                        deleteTeamMutation(team.id);
                      },
                    });
                  }
                }}
                options={[
                  { label: 'Edit team', value: 'edit' },
                  { label: 'Delete team', value: 'delete' },
                ]}
              />
            )}
          </Stack>
          <Stack>
            {renderDriversInTeam({
              name: team.name,
              driver_info: team.driver_info,
            })}
          </Stack>
        </Stack>
      );
    });
  }, [teams, searchTeamQuery, isEmptyState]);

  if (isFirstLoading)
    return (
      <CircularLoading loading={isFetchingTeams} className={classes.loading} />
    );

  return (
    <Container className={classes.containerClass}>
      <Stack
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.6,
        }}
      >
        {teamListComponent}
        <Button
          fullWidth={false}
          onClick={() => {
            if (isStarterOrStandardPlan) {
              setIsOpenUpSellDialog(true);
            } else {
              setTeamEditData(null);
              toggleOpenAddTeamDialog();
            }
          }}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.4,
            width: 'fit-content',
          }}
        >
          <img src={AddIcon} alt='add-icon' />
          Add Team
        </Button>
        {isEmptyState && <SetupYourTeams setOpen={setOpenAddTeamDialog} />}
        {openAddTeamDialog && (
          <DialogAddTeam
            teamEditData={teamEditData}
            open={openAddTeamDialog}
            onClose={toggleOpenAddTeamDialog}
          />
        )}
        <CircularLoading loading={isFetchingTeams} />
      </Stack>
    </Container>
  );
};
